import type { ReactNode } from 'react';
import { Button, Stack, Text } from '@mantine/core';
import { PageHeader } from './PageHeader';
import { PageSkeleton } from './PageSkeleton';
import { resolveListState, type ListPageQueryState } from './listPageContract';

/**
 * Einheitliche Fehlerfläche des Zustands-Kontrakts (A2-13): der globale
 * A2-6-Toast meldet den Fehler bereits — diese Fläche ersetzt zusätzlich die
 * sonst leere Tabelle am Ort der Liste und bietet „Erneut laden" an.
 */
function ListErrorCard({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <div className="wb-table-card wb-empty" data-testid="list-error">
      <svg
        className="wb-error-illustration"
        width="72"
        height="48"
        viewBox="0 0 72 48"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M36 7 L59 41 H13 z" />
        <path d="M36 19v10" />
        <path d="M36 34.5v0.01" />
      </svg>
      <Text size="sm">{text}</Text>
      <div style={{ marginTop: 'var(--wb-space-md)' }}>
        <Button variant="default" onClick={onRetry} data-testid="list-error-retry">
          Erneut laden
        </Button>
      </div>
    </div>
  );
}

/**
 * Zustandsweiche des Kontrakts, auch einzeln nutzbar (z. B. für eine zweite
 * Listen-Karte auf derselben Seite wie im Mahnwesen): Laden → PageSkeleton,
 * Fehler → Fehlerfläche mit Retry, Leer → `empty`, sonst `children`.
 */
export function ListStates({
  state,
  errorText,
  empty,
  children,
}: {
  state: ListPageQueryState;
  errorText: string;
  empty: ReactNode;
  children: ReactNode;
}) {
  switch (resolveListState(state)) {
    case 'loading':
      return <PageSkeleton />;
    case 'error':
      return <ListErrorCard text={errorText} onRetry={state.retry} />;
    case 'empty':
      return <>{empty}</>;
    case 'filled':
      return <>{children}</>;
  }
}

/**
 * ListPage-Gerüst (D-W1 Welle 2, A2-13): EIN Träger für die klassischen
 * Listen-Seiten — PageHeader (Titel, Beschreibung/Filter-Meta, genau eine
 * Aktion), optionale Tab-Zeile, Filterleiste-Slot (das Gerüst stellt die
 * wb-filterbar), optionaler Banner und der Zustands-Kontrakt mit vier
 * Zuständen. `state`, `errorText`, `empty` und `children` sind bewusst
 * Pflicht-Props: eine Seite, die einen Zustand nicht bedient, kompiliert
 * nicht. Dialoge/Modals bleiben Geschwister der ListPage (Portale, kein
 * Layout-Anteil); in-Fluss-Inhalt nach der Liste (zweite Karte) gehört in
 * `after`. Registrierung neuer Seiten: listPageContract.ts.
 */
export function ListPage({
  title,
  meta,
  action,
  tabs,
  filters,
  banner,
  state,
  errorText,
  empty,
  children,
  after,
}: {
  title: string;
  meta: ReactNode;
  /** Genau eine Aktion rechts im Kopf (Brief §2). */
  action?: ReactNode;
  /** Optionale Tab-Zeile zwischen Kopf und Filterleiste (z. B. Belegarten). */
  tabs?: ReactNode;
  /** Inhalt der Filterleiste; das Gerüst rendert die wb-filterbar darum. */
  filters?: ReactNode;
  /** Optionaler Hinweis-/Fehlerbanner oberhalb der Liste (z. B. Lauf-Ergebnis). */
  banner?: ReactNode;
  state: ListPageQueryState;
  errorText: string;
  /** Leerzustand inklusive seiner Karte (i. d. R. TableCard + EmptyState). */
  empty: ReactNode;
  /** Gefüllt: die TableCard mit der eigentlichen Liste. */
  children: ReactNode;
  /** In-Fluss-Inhalt nach der Liste (z. B. zweite Listen-Karte). */
  after?: ReactNode;
}) {
  return (
    <Stack gap="lg">
      <PageHeader title={title} meta={meta} action={action} />
      {tabs}
      {filters && <div className="wb-filterbar">{filters}</div>}
      {banner}
      <ListStates state={state} errorText={errorText} empty={empty}>
        {children}
      </ListStates>
      {after}
    </Stack>
  );
}
